import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { StatusBadge } from "../components/Badges";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import Integrations from "./Integrations";

function CategoryIcon({ name, className }) {
  const C = Icons[name] || Icons.Plug;
  return <C className={className} strokeWidth={1.75} />;
}

const HEALTH_COLORS = {
  healthy: { color: "var(--success)", bg: "var(--success-soft)", label: "Healthy" },
  error: { color: "var(--danger)", bg: "var(--danger-soft)", label: "Error" },
  unknown: { color: "var(--muted)", bg: "var(--bg-2)", label: "Not tested" },
  disabled: { color: "var(--muted)", bg: "var(--bg-2)", label: "Disabled" },
};

function HealthPill({ status, enabled }) {
  const key = !enabled ? "disabled" : (status || "unknown");
  const t = HEALTH_COLORS[key] || HEALTH_COLORS.unknown;
  return (
    <span data-testid={`connector-health-${key}`}
      className="inline-flex items-center gap-1.5 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ color: t.color, background: t.bg }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {t.label}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId }) {
  const toneColor = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1 ink" style={{ color: toneColor }}>{value}</div>
    </div>
  );
}

export default function Connectors() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = ["super_admin", "org_admin"].includes(user?.role);

  const [tab, setTab] = useState("active"); // active | catalog
  const [instances, setInstances] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [selected, setSelected] = useState(null); // definition selected from catalog
  const [showConfig, setShowConfig] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setInstances(null);
    try {
      const [inst, cat, sum] = await Promise.all([
        api.get("/connectors"),
        api.get("/connectors/catalog"),
        api.get("/connectors/summary/health"),
      ]);
      setInstances(inst.data.items || []);
      setCatalog(cat.data);
      setSummary(sum.data);
    } catch (e) {
      setInstances([]);
      toast.error("Failed to load connectors");
    }
  };
  useEffect(() => { load(); }, []);

  const filteredCatalog = useMemo(() => {
    if (!catalog) return [];
    return catalog.items.filter((c) => {
      if (categoryFilter && c.category !== categoryFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        return c.name.toLowerCase().includes(s) || c.vendor.toLowerCase().includes(s) || c.category.toLowerCase().includes(s);
      }
      return true;
    });
  }, [catalog, q, categoryFilter]);

  const filteredInstances = useMemo(() => {
    if (!instances) return [];
    return instances.filter((c) => {
      if (categoryFilter && c.category !== categoryFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        return c.name.toLowerCase().includes(s) || c.connector_type.toLowerCase().includes(s);
      }
      return true;
    });
  }, [instances, q, categoryFilter]);

  const openConfigure = (defn) => {
    setSelected(defn);
    const initial = {};
    (defn.config_fields || []).forEach((f) => { initial[f.key] = f.default ?? ""; });
    setForm({ display_name: defn.name, sync_frequency: "manual", ...initial });
    setShowConfig(true);
  };

  const saveConnector = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const config = {};
      selected.config_fields.forEach((f) => { config[f.key] = form[f.key]; });
      const body = {
        connector_type: selected.id,
        display_name: form.display_name || selected.name,
        config,
        sync_frequency: form.sync_frequency || "manual",
        enabled: true,
      };
      const { data } = await api.post("/connectors", body);
      toast.success(`${data.name} added`);
      setShowConfig(false);
      setSelected(null);
      await load();
      nav(`/app/connectors/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add connector");
    } finally {
      setSaving(false);
    }
  };

  const quickTest = async (c) => {
    toast.info(`Testing ${c.name}...`);
    try {
      const { data } = await api.post(`/connectors/${c.id}/test`);
      if (data.ok) toast.success(`${c.name}: ${data.message || "Connected"} (${data.latency_ms || 0} ms)`);
      else toast.error(`${c.name}: ${data.message || "Test failed"}`);
      await load();
    } catch (e) { toast.error("Test failed"); }
  };

  const quickSync = async (c) => {
    toast.info(`Sync started for ${c.name}...`);
    try {
      const { data } = await api.post(`/connectors/${c.id}/sync`);
      if (data.ok) toast.success(`${c.name}: pulled ${data.total_records} records`);
      else toast.error(`${c.name}: sync completed with errors (${data.total_records} records)`);
      await load();
    } catch (e) { toast.error("Sync failed"); }
  };

  const toggleEnabled = async (c) => {
    try {
      await api.post(`/connectors/${c.id}/${c.enabled ? "disable" : "enable"}`);
      toast.success(`${c.name}: ${c.enabled ? "disabled" : "enabled"}`);
      await load();
    } catch { toast.error("Failed to update"); }
  };

  const revoke = async (c) => {
    if (!window.confirm(`Revoke and remove "${c.name}"? Stored credentials will be deleted.`)) return;
    try {
      await api.delete(`/connectors/${c.id}`);
      toast.success("Connector revoked");
      await load();
    } catch { toast.error("Failed to revoke"); }
  };

  if (instances === null || catalog === null) return <Loader />;

  const stats = summary?.by_status || {};

  return (
    <div>
      <PageHeader
        title="Enterprise Connectors"
        kicker="Integrations"
        description="Connect identity, productivity, ticketing, security, cloud and CRM systems to power automated data discovery, DSAR fulfilment and breach signals."
        testId="connectors-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={load} data-testid="connectors-refresh">
              <Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Btn>
            {isAdmin && (
              <Btn onClick={() => setTab("catalog")} data-testid="connectors-add-btn">
                <Icons.Plus className="w-3.5 h-3.5" /> Add connector
              </Btn>
            )}
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Configured" value={summary?.total ?? 0} testId="stat-total" />
        <StatCard label="Healthy" value={stats.healthy ?? 0} tone="success" testId="stat-healthy" />
        <StatCard label="Errors" value={stats.error ?? 0} tone="danger" testId="stat-error" />
        <StatCard label="Disabled" value={stats.disabled ?? 0} testId="stat-disabled" />
      </div>

      {/* Tabs */}
      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {["active", "catalog", "legacy"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className="px-3 py-1.5 text-xs font-medium rounded-md transition-all"
            style={{
              background: tab === t ? "var(--primary-soft)" : "transparent",
              color: tab === t ? "var(--primary)" : "var(--muted)",
            }}
          >
            {t === "active" ? `Configured (${instances.length})` : t === "catalog" ? `Catalog (${catalog.items.length})` : "Legacy registry"}
          </button>
        ))}
      </div>

      {tab === "legacy" ? <Integrations embedded /> : (<>
      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 muted-2" />
          <TextInput
            data-testid="connectors-search"
            className="pl-9"
            placeholder={tab === "active" ? "Search configured connectors..." : "Search catalog (e.g. jira, salesforce, aws)..."}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <SelectInput
          data-testid="connectors-category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ maxWidth: 240 }}
        >
          <option value="">All categories</option>
          {catalog.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </SelectInput>
      </div>

      {tab === "active" ? (
        filteredInstances.length === 0 ? (
          <EmptyState
            title="No connectors configured yet"
            hint="Browse the catalog to connect your first identity provider, ticketing system, cloud, or CRM."
            action={isAdmin && <Btn onClick={() => setTab("catalog")} data-testid="empty-add-btn"><Icons.Plus className="w-4 h-4"/> Browse catalog</Btn>}
          />
        ) : (
          <div className="grid gap-3">
            {filteredInstances.map((c) => (
              <div key={c.id} className="surface rounded-xl p-4 flex items-center gap-4 hover:card-shadow transition-all"
                style={{ border: "1px solid var(--border)" }}
                data-testid={`connector-row-${c.id}`}>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                  <CategoryIcon name={c.definition?.icon || "Plug"} className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/app/connectors/${c.id}`} className="font-semibold ink text-[14px] hover:underline">
                      {c.name}
                    </Link>
                    <HealthPill status={c.health} enabled={c.enabled} />
                    <span className="text-[11px] muted">· {c.category}</span>
                    <span className="text-[11px] muted">· {c.definition?.auth_type}</span>
                  </div>
                  <div className="mt-1 text-[12px] muted flex items-center gap-3 flex-wrap">
                    <span><Icons.Clock className="w-3 h-3 inline mr-1" />
                      Last sync: {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : "never"}
                    </span>
                    {c.next_run_at && (
                      <span><Icons.CalendarClock className="w-3 h-3 inline mr-1" />
                        Next: {new Date(c.next_run_at).toLocaleString()}
                      </span>
                    )}
                    <span><Icons.Database className="w-3 h-3 inline mr-1" />
                      {c.last_sync_records || 0} records
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isAdmin && (
                    <>
                      <Btn variant="secondary" size="sm" onClick={() => quickTest(c)} data-testid={`test-btn-${c.id}`}>
                        <Icons.PlugZap className="w-3.5 h-3.5" /> Test
                      </Btn>
                      <Btn variant="secondary" size="sm" onClick={() => quickSync(c)} data-testid={`sync-btn-${c.id}`}>
                        <Icons.RotateCw className="w-3.5 h-3.5" /> Sync
                      </Btn>
                    </>
                  )}
                  <Link to={`/app/connectors/${c.id}`}>
                    <Btn variant="secondary" size="sm" data-testid={`configure-btn-${c.id}`}>
                      <Icons.Settings2 className="w-3.5 h-3.5" /> {isAdmin ? "Configure" : "View"}
                    </Btn>
                  </Link>
                  {isAdmin && (
                    <>
                      <button
                        title={c.enabled ? "Disable" : "Enable"}
                        onClick={() => toggleEnabled(c)}
                        data-testid={`toggle-btn-${c.id}`}
                        className="p-1.5 rounded-md muted hover:ink hover:bg-app transition-all"
                      >
                        {c.enabled ? <Icons.PauseCircle className="w-4 h-4"/> : <Icons.PlayCircle className="w-4 h-4"/>}
                      </button>
                      <button
                        title="Revoke"
                        onClick={() => revoke(c)}
                        data-testid={`revoke-btn-${c.id}`}
                        className="p-1.5 rounded-md muted hover:danger hover:bg-app transition-all"
                      >
                        <Icons.Trash2 className="w-4 h-4"/>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredCatalog.map((c) => (
            <div key={c.id} className="surface rounded-xl p-4 flex flex-col gap-3 hover:card-shadow transition-all"
              style={{ border: "1px solid var(--border)" }}
              data-testid={`catalog-item-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                  <CategoryIcon name={c.icon} className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold ink text-[13.5px] leading-tight">{c.name}</div>
                  <div className="text-[11px] muted mt-0.5">{c.vendor} · {c.category}</div>
                </div>
              </div>
              <div className="text-[12px] muted leading-relaxed line-clamp-2">{c.description}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-md text-[10px] font-medium"
                  style={{ background: "var(--bg-2)", color: "var(--muted)" }}>
                  {c.auth_type.replace("_", " ")}
                </span>
                {c.has_oauth && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-medium"
                    style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                    OAuth 2.0
                  </span>
                )}
              </div>
              <div className="mt-auto pt-2">
                {isAdmin ? (
                  <Btn size="sm" className="w-full justify-center" onClick={() => openConfigure(c)} data-testid={`connect-btn-${c.id}`}>
                    <Icons.Plus className="w-3.5 h-3.5" /> Connect
                  </Btn>
                ) : (
                  <span className="text-[11px] muted">Admin only</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}

      {/* Configure Modal */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Configure — {selected?.name}</DialogTitle>
            <DialogDescription>
              Credentials are encrypted at rest with Fernet (AES-128).
              You can re-run tests any time; OAuth flows require completing authorization after saving.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 max-h-[62vh] overflow-y-auto pr-1">
            <Field label="Display name">
              <TextInput
                data-testid="field-display_name"
                value={form.display_name || ""}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </Field>
            {(selected?.config_fields || []).map((f) => (
              <Field key={f.key} label={f.label} required={f.required}>
                {f.type === "textarea" ? (
                  <TextArea
                    data-testid={`field-${f.key}`}
                    value={form[f.key] || ""}
                    placeholder={f.placeholder}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                ) : f.type === "select" ? (
                  <SelectInput
                    data-testid={`field-${f.key}`}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  >
                    <option value="">-- Select --</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </SelectInput>
                ) : f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid={`field-${f.key}`} type="checkbox"
                      checked={!!form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} />
                    <span className="muted">{f.label}</span>
                  </label>
                ) : f.type === "number" ? (
                  <TextInput
                    data-testid={`field-${f.key}`}
                    type="number"
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                ) : (
                  <TextInput
                    data-testid={`field-${f.key}`}
                    type={f.secret ? "password" : "text"}
                    placeholder={f.placeholder}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                )}
                {f.help && <div className="text-[11px] muted mt-1">{f.help}</div>}
              </Field>
            ))}
            <Field label="Sync frequency">
              <SelectInput
                data-testid="field-sync_frequency"
                value={form.sync_frequency || "manual"}
                onChange={(e) => setForm({ ...form, sync_frequency: e.target.value })}
              >
                <option value="manual">Manual only</option>
                <option value="every_15_min">Every 15 minutes</option>
                <option value="hourly">Hourly</option>
                <option value="every_6_hours">Every 6 hours</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </SelectInput>
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setShowConfig(false)} data-testid="cancel-configure-btn">Cancel</Btn>
            <Btn onClick={saveConnector} disabled={saving} data-testid="save-configure-btn">
              {saving ? "Saving…" : "Save & Continue"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
